import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { Tenant, TenantInfo, UserInfo } from '../types';
import { addRecentTenant, detectSubdomain } from '../utils/tenantUtils';
import { getApiBaseUrl } from '../utils/apiConfig';

/**
 * SECURITY: Tenant Context for Multi-Tenant Isolation
 *
 * Key Security Principles:
 * 1. Regular users NEVER see a tenant selection - they're auto-routed to their tenant
 * 2. Only SuperAdmin users can switch between tenants (in admin portal)
 * 3. Tenant info comes from the authenticated login response
 * 4. Users cannot access data from other tenants
 */

interface TenantContextValue {
  currentTenant: Tenant | null;
  availableTenants: Tenant[];
  switchTenant: (tenantId: number) => void;
  isLoading: boolean;
  refreshTenants: () => Promise<void>;
  isSuperAdmin: boolean;
}

const TenantContext = createContext<TenantContextValue | undefined>(undefined);

// Helper to check if user is SuperAdmin
const checkIsSuperAdmin = (): boolean => {
  try {
    const userStr = localStorage.getItem('mdm_user');
    if (!userStr) return false;
    const user = JSON.parse(userStr) as UserInfo;
    return user?.roles?.includes('SuperAdmin') || false;
  } catch {
    return false;
  }
};

// Convert TenantInfo (from login) to full Tenant object
const tenantInfoToTenant = (info: TenantInfo): Tenant => ({
  tenantId: info.tenantId,
  tenantCode: info.tenantCode,
  tenantName: info.tenantName,
  description: '',
  isActive: true,
  createdDate: '',
  createdBy: '',
  lastModifiedDate: '',
  lastModifiedBy: '',
});

export const TenantProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [currentTenant, setCurrentTenant] = useState<Tenant | null>(null);
  const [availableTenants, setAvailableTenants] = useState<Tenant[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [_isPolling, setIsPolling] = useState(false);

  useEffect(() => {
    initializeTenant();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // CRITICAL: Re-initialize when auth token or tenant appears (after login)
  // This polling mechanism handles the race condition where AuthContext stores tenant
  // in localStorage AFTER TenantContext's initial load completes.
  useEffect(() => {
    let isMounted = true;
    let foundTenant = false;

    const checkAuth = () => {
      if (!isMounted || foundTenant) return;

      const token = localStorage.getItem('mdm_auth_token');
      const storedTenant = localStorage.getItem('mdm_tenant');

      // If we have a token and tenant but currentTenant isn't set, reload tenant context
      if (token && storedTenant && !currentTenant) {
        console.log('Auth token and tenant detected in localStorage, reloading tenant...');
        // Parse and set tenant directly to avoid race conditions
        try {
          const tenantInfo = JSON.parse(storedTenant);
          if (tenantInfo && tenantInfo.tenantId) {
            foundTenant = true;
            const tenant = {
              tenantId: tenantInfo.tenantId,
              tenantCode: tenantInfo.tenantCode,
              tenantName: tenantInfo.tenantName,
              description: '',
              isActive: true,
              createdDate: '',
              createdBy: '',
              lastModifiedDate: '',
              lastModifiedBy: '',
            };
            setCurrentTenant(tenant);
            setAvailableTenants([tenant]);
            localStorage.setItem('selectedTenantId', tenant.tenantId.toString());
            localStorage.setItem('selectedTenantCode', tenant.tenantCode || '');
            setIsPolling(false);
            setIsLoading(false);
            console.log('✓ Tenant set from polling:', tenant.tenantName);
          }
        } catch (e) {
          console.error('Failed to parse tenant from localStorage:', e);
        }
      }
    };

    // Check immediately
    checkAuth();

    // Poll every 200ms for same-tab localStorage changes (race condition fix)
    // 50ms was too aggressive and caused performance issues
    const pollInterval = setInterval(checkAuth, 200);

    // Keep polling for 5 seconds to handle slow connections (reduced from 15s)
    const cleanupTimeout = setTimeout(() => {
      clearInterval(pollInterval);
      if (isMounted && !foundTenant) {
        setIsPolling(false);
      }
    }, 5000);

    // Also listen for cross-tab changes
    window.addEventListener('storage', checkAuth);

    return () => {
      isMounted = false;
      window.removeEventListener('storage', checkAuth);
      clearInterval(pollInterval);
      clearTimeout(cleanupTimeout);
    };
  }, [currentTenant]);

  // Cross-tab synchronization
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'selectedTenantId' && e.newValue !== e.oldValue) {
        if (e.newValue === null) {
          setCurrentTenant(null);
        } else {
          window.location.reload();
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, []);

  const initializeTenant = async () => {
    try {
      setIsLoading(true);
      const superAdmin = checkIsSuperAdmin();
      setIsSuperAdmin(superAdmin);

      await loadTenants(superAdmin);
    } catch (error) {
      console.error('Failed to initialize tenant context:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const loadTenants = async (superAdmin: boolean) => {
    try {
      const token = localStorage.getItem('mdm_auth_token');
      if (!token) {
        console.log('No auth token found, skipping tenant load');
        return;
      }

      const API_BASE_URL = getApiBaseUrl();

      // PRIORITY 1: Check if tenant was provided in login response
      const storedTenantInfo = localStorage.getItem('mdm_tenant');
      if (storedTenantInfo) {
        try {
          const tenantInfo = JSON.parse(storedTenantInfo) as TenantInfo;
          if (tenantInfo && tenantInfo.tenantId) {
            console.log('✓ Using tenant from login response:', tenantInfo.tenantName);
            const tenant = tenantInfoToTenant(tenantInfo);
            setCurrentTenant(tenant);
            setAvailableTenants([tenant]); // SECURITY: Only user's own tenant
            localStorage.setItem('selectedTenantId', tenant.tenantId.toString());
            localStorage.setItem('selectedTenantCode', tenant.tenantCode || '');
            return;
          }
        } catch (e) {
          console.error('Failed to parse stored tenant info:', e);
        }
      }

      // PRIORITY 2: Check subdomain-based access
      const subdomainContext = detectSubdomain();
      if (subdomainContext.isTenantLogin && subdomainContext.subdomain) {
        console.log(`Accessing via subdomain: ${subdomainContext.subdomain}`);
        await loadTenantBySubdomain(subdomainContext.subdomain);
        return;
      }

      // PRIORITY 3: SuperAdmin only - load all tenants for selection
      if (superAdmin) {
        console.log('SuperAdmin detected, loading all tenants for selection');
        const headers: HeadersInit = { Authorization: `Bearer ${token}` };
        const response = await fetch(`${API_BASE_URL}/Tenants`, { headers });

        if (response.ok) {
          const data = await response.json();
          const tenants = data.data || data;
          setAvailableTenants(Array.isArray(tenants) ? tenants : []);

          // Try to restore stored tenant selection for SuperAdmin
          const storedTenantId = localStorage.getItem('selectedTenantId');
          if (storedTenantId && Array.isArray(tenants)) {
            const tenant = tenants.find((t: Tenant) => t.tenantId === parseInt(storedTenantId));
            if (tenant) {
              console.log('✓ Restored SuperAdmin tenant selection:', tenant.tenantName);
              setCurrentTenant(tenant);
            }
          }
        }
        return;
      }

      // PRIORITY 4: Regular user without tenant info - this shouldn't happen
      // If we get here, the user should have received tenant info from login
      console.warn('SECURITY: User has no tenant assigned - contact administrator');
      setAvailableTenants([]);
    } catch (error) {
      console.error('Failed to load tenants:', error);
      setAvailableTenants([]);
    }
  };

  const loadTenantBySubdomain = async (subdomain: string) => {
    try {
      const API_BASE_URL = getApiBaseUrl();
      const token = localStorage.getItem('mdm_auth_token');
      const headers: HeadersInit = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }

      const response = await fetch(`${API_BASE_URL}/Tenants/by-subdomain/${subdomain}`, {
        headers,
      });

      if (!response.ok) {
        throw new Error(`Tenant not found for subdomain: ${subdomain}`);
      }

      const data = await response.json();
      const tenant = data.data || data;

      if (tenant && tenant.tenantId) {
        console.log(`✓ Auto-resolved tenant from subdomain '${subdomain}':`, tenant.tenantName);
        setCurrentTenant(tenant);
        setAvailableTenants([tenant]); // SECURITY: Only this tenant
        localStorage.setItem('selectedTenantId', tenant.tenantId.toString());
        localStorage.setItem('selectedTenantCode', tenant.tenantCode || '');
      } else {
        throw new Error(`Invalid tenant data for subdomain: ${subdomain}`);
      }
    } catch (error) {
      console.error('Failed to load tenant by subdomain:', error);
      throw error;
    }
  };

  const switchTenant = (tenantId: number) => {
    // SECURITY: Only SuperAdmin can switch tenants
    if (!isSuperAdmin) {
      console.error('SECURITY: Non-SuperAdmin attempted to switch tenant');
      return;
    }

    const tenant = availableTenants.find(t => t.tenantId === tenantId);
    if (tenant) {
      setCurrentTenant(tenant);
      localStorage.setItem('selectedTenantId', tenantId.toString());
      localStorage.setItem('selectedTenantCode', tenant.tenantCode || '');
      addRecentTenant(tenantId);
      window.location.reload();
    }
  };

  const refreshTenants = async () => {
    await loadTenants(isSuperAdmin);
  };

  return (
    <TenantContext.Provider
      value={{
        currentTenant,
        availableTenants,
        switchTenant,
        isLoading,
        refreshTenants,
        isSuperAdmin,
      }}
    >
      {children}
    </TenantContext.Provider>
  );
};

export const useTenant = () => {
  const context = useContext(TenantContext);
  if (!context) {
    throw new Error('useTenant must be used within TenantProvider');
  }
  return context;
};
